import {
  StreamingOverviewResponse,
  StreamingSeriesPoint,
  StreamingSource,
  StreamingTotal,
  StreamingTotalKey,
} from 'fym-shared/src/music24/streaming';
import { isTileDrawn } from './streamingSources';

/** The totals of the first row and of the second row (design a1, direction A). */
export const FIRST_ROW: readonly StreamingTotalKey[] = ['streams', 'listeners', 'followers', 'subscribers'];
export const SECOND_ROW: readonly StreamingTotalKey[] = ['saves', 'playlistAdds', 'monthlyListeners', 'downloads'];

/** The totals that add up day by day. Only these can be summed again from the series. */
const FLOW_KEYS: ReadonlyArray<StreamingTotalKey & keyof StreamingSeriesPoint> = [
  'streams',
  'saves',
  'playlistAdds',
  'downloads',
];

export interface StreamingTileModel {
  key: StreamingTotalKey;
  total: StreamingTotal;
  /** The sources the tile names in its "from" line. */
  sources: string[];
  /**
   * `reconnect` or `disconnected` sources that are the only feed of this tile: the tile shows
   * "Stopped {date}".
   */
  stoppedAt: string | null;
  /** True when a source of the tile is turned off: the change is not shown. */
  filtered: boolean;
}

function sumOfSeries(
  series: StreamingOverviewResponse['series'],
  sources: string[],
  key: StreamingTotalKey & keyof StreamingSeriesPoint
): number | null {
  let sum: number | null = null;
  series
    .filter((one) => sources.includes(one.integrationType))
    .forEach((one) =>
      one.points.forEach((point) => {
        const value = point[key];
        if (typeof value === 'number') {
          sum = (sum ?? 0) + value;
        }
      })
    );
  return sum;
}

/** The day the numbers of a tile stop, when every source of the tile is signed out or disconnected. */
function stoppedAtOf(sources: string[], all: StreamingSource[]): string | null {
  const own = all.filter((source) => sources.includes(source.integrationType));
  if (own.length === 0 || own.some((source) => source.state !== 'reconnect' && source.state !== 'disconnected')) {
    return null;
  }
  return own.map((source) => source.stoppedAt).find((day): day is string => day !== null) ?? null;
}

/**
 * The tiles to draw, in order. A tile whose sources are not connected is left out (design §4.2).
 * A source the reader turned off (`off`) is taken out of a flow tile (streams, saves, playlist
 * adds, downloads): the flow is added again from the series of the other sources, and the change
 * is not shown, because the previous period has no split by source. A flow tile with every source
 * turned off is left out. The other tiles do not change.
 */
export function buildStreamingTiles(
  overview: StreamingOverviewResponse,
  keys: readonly StreamingTotalKey[],
  off: readonly string[]
): StreamingTileModel[] {
  return keys
    .filter((key) => isTileDrawn(key, overview.sources))
    .flatMap((key): StreamingTileModel[] => {
      const total = overview.totals[key];
      const flowKey = FLOW_KEYS.find((one) => one === key);
      // A level (followers) or a unique count (listeners) has no split by source in the answer,
      // so the filter does not change it.
      const kept = flowKey === undefined ? total.sources : total.sources.filter((source) => !off.includes(source));
      const filtered = kept.length < total.sources.length;
      if (filtered && kept.length === 0) {
        return [];
      }
      const shown: StreamingTotal =
        filtered && flowKey !== undefined
          ? { current: sumOfSeries(overview.series, kept, flowKey), previous: null, changePct: null, sources: kept }
          : total;
      return [{ key, total: shown, sources: kept, stoppedAt: stoppedAtOf(kept, overview.sources), filtered }];
    });
}
